import { useState, useEffect, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import { getSupabaseClient } from "../../../lib/supabaseClient";
import type { BookingConfirmation, ConfirmationStatus } from "../../../store/types";

interface AssignmentRow {
  id: string;
  order_id: string;
  order_number: string;
  photographer_id: string;
  photographer_name: string;
  selected_city: string;
  shoot_date: string;
  confirmation_status: ConfirmationStatus;
  note: string;
}

interface ActivePhotographer { id: string; display_name: string; }

const STATUS_COLORS: Record<string, string> = {
  assigned: "bg-amber-100 text-amber-700",
  confirmed: "bg-green-100 text-green-700",
  completed: "bg-[#C8B8A6]/20 text-[#7A6B5A]",
  declined: "bg-red-100 text-red-600",
  replacement_needed: "bg-orange-100 text-orange-700",
};

const STATUS_LABELS: Record<ConfirmationStatus, string> = {
  pending: "已指派",
  confirmed: "已確認",
  declined: "已拒絕",
  replacement_needed: "需替換",
};

function getDisplayStatus(status: ConfirmationStatus): string {
  return STATUS_LABELS[status] ?? status;
}

function getStatusColorClass(status: ConfirmationStatus): string {
  const map: Record<ConfirmationStatus, string> = {
    pending: STATUS_COLORS.assigned,
    confirmed: STATUS_COLORS.confirmed,
    declined: STATUS_COLORS.declined,
    replacement_needed: STATUS_COLORS.replacement_needed,
  };
  return map[status] ?? "bg-gray-100 text-gray-600";
}

export default function AdminAssignmentsPage() {
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [photographers, setPhotographers] = useState<ActivePhotographer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [editTarget, setEditTarget] = useState<AssignmentRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ photographer_id: "", confirmation_status: "pending" as ConfirmationStatus, note: "" });
  const [addForm, setAddForm] = useState({ order_id: "", photographer_id: "", note: "" });
  const [orders, setOrders] = useState<{ id: string; order_number: string; selected_city: string; shoot_date: string }[]>([]);
  const [saving, setSaving] = useState(false);

  const supabase = getSupabaseClient();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bcRes, ordersRes, phRes] = await Promise.all([
        supabase.from("booking_confirmations").select("*").order("created_at", { ascending: false }),
        supabase.from("orders").select("id,order_number,selected_city,shoot_date"),
        supabase.from("photographers").select("id,display_name").eq("is_active", true).order("display_name"),
      ]);
      if (bcRes.error) throw bcRes.error;
      if (ordersRes.error) throw ordersRes.error;

      const orderMap = new Map((ordersRes.data ?? []).map((o: { id: string; order_number: string; selected_city: string; shoot_date: string }) => [o.id, o]));
      const phMap = new Map((phRes.data ?? []).map((p: { id: string; display_name: string }) => [p.id, p.display_name]));

      const enriched: AssignmentRow[] = (bcRes.data ?? []).map((bc: BookingConfirmation) => {
        const o = orderMap.get(bc.order_id) as { order_number: string; selected_city: string; shoot_date: string } | undefined;
        return {
          id: bc.id,
          order_id: bc.order_id,
          order_number: o?.order_number ?? "—",
          photographer_id: bc.photographer_id,
          photographer_name: phMap.get(bc.photographer_id) ?? "未指派",
          selected_city: o?.selected_city ?? "—",
          shoot_date: o?.shoot_date ?? "—",
          confirmation_status: bc.confirmation_status,
          note: bc.note,
        };
      });

      setRows(enriched);
      setPhotographers((phRes.data ?? []) as ActivePhotographer[]);
      setOrders((ordersRes.data ?? []) as { id: string; order_number: string; selected_city: string; shoot_date: string }[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "載入失敗");
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = filterStatus === "all" ? rows : rows.filter(r => r.confirmation_status === filterStatus);

  const openEdit = (row: AssignmentRow) => {
    setEditTarget(row);
    setEditForm({ photographer_id: row.photographer_id, confirmation_status: row.confirmation_status, note: row.note });
    setModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("booking_confirmations").update({
        photographer_id: editForm.photographer_id,
        confirmation_status: editForm.confirmation_status,
        note: editForm.note,
        updated_at: new Date().toISOString(),
        confirmed_at: editForm.confirmation_status === "confirmed" ? new Date().toISOString() : null,
      }).eq("id", editTarget.id);
      if (error) throw error;
      setModalOpen(false);
      await fetchData();
    } catch (e) {
      alert(e instanceof Error ? e.message : "儲存失敗");
    } finally {
      setSaving(false);
    }
  };

  const handleAddAssignment = async () => {
    if (!addForm.order_id || !addForm.photographer_id) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("booking_confirmations").insert({
        order_id: addForm.order_id,
        photographer_id: addForm.photographer_id,
        confirmation_status: "pending",
        note: addForm.note,
        replacement_required: false,
      });
      if (error) throw error;
      setAddModalOpen(false);
      setAddForm({ order_id: "", photographer_id: "", note: "" });
      await fetchData();
    } catch (e) {
      alert(e instanceof Error ? e.message : "儲存失敗");
    } finally {
      setSaving(false);
    }
  };

  const FILTER_TABS = [
    { value: "all", label: "全部" },
    { value: "pending", label: "已指派" },
    { value: "confirmed", label: "已確認" },
    { value: "declined", label: "已拒絕" },
    { value: "replacement_needed", label: "需替換" },
  ];

  return (
    <AdminLayout
      title="指派管理"
      subtitle="管理攝影師指派 — 指派狀態、確認與完成追蹤"
      action={
        <button onClick={() => setAddModalOpen(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#2C2C2C] text-white text-sm font-light rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#3C3C3C] transition-colors">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></div>
          新增指派
        </button>
      }
    >
      {/* Filter Tabs */}
      <div className="flex items-center gap-1 bg-[#F7F6F3] rounded-lg p-1 mb-5 w-fit">
        {FILTER_TABS.map(tab => (
          <button key={tab.value} onClick={() => setFilterStatus(tab.value)}
            className={`px-3 py-1.5 text-xs font-light rounded-md transition-colors cursor-pointer whitespace-nowrap ${
              filterStatus === tab.value ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64 text-[#2C2C2C]/40 text-sm font-light">載入中...</div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <p className="text-sm text-red-500 font-light">{error}</p>
          <button onClick={fetchData} className="text-sm text-[#2C2C2C]/60 underline cursor-pointer">重試</button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-[#D9D9D9]/60 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#D9D9D9]/60">
                {["訂單編號","攝影師","城市","拍攝日期","狀態","備註","操作"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-light text-[#2C2C2C]/50 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-[#2C2C2C]/30 font-light">尚無指派記錄</td></tr>
              ) : filtered.map(row => (
                <tr key={row.id} className="border-b border-[#D9D9D9]/30 hover:bg-[#F7F6F3]/60 transition-colors">
                  <td className="px-4 py-3 font-light text-[#2C2C2C]/80">{row.order_number}</td>
                  <td className="px-4 py-3 font-light text-[#2C2C2C]/80">{row.photographer_name}</td>
                  <td className="px-4 py-3 font-light text-[#2C2C2C]/60">{row.selected_city}</td>
                  <td className="px-4 py-3 font-light text-[#2C2C2C]/60 whitespace-nowrap">{row.shoot_date}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-light ${getStatusColorClass(row.confirmation_status)}`}>
                      {getDisplayStatus(row.confirmation_status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-light text-[#2C2C2C]/50 max-w-[140px] truncate">{row.note || "—"}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => openEdit(row)}
                      className="w-7 h-7 flex items-center justify-center text-[#2C2C2C]/40 hover:text-[#2C2C2C] transition-colors cursor-pointer rounded">
                      <i className="ri-edit-line text-sm" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {modalOpen && editTarget && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-lg">
            <div className="px-6 py-4 border-b border-[#D9D9D9]/60">
              <h2 className="text-base font-light text-[#2C2C2C] tracking-wide">編輯指派</h2>
              <p className="text-xs text-[#2C2C2C]/40 font-light mt-0.5">{editTarget.order_number}</p>
            </div>
            <div className="px-6 py-5 flex flex-col gap-4">
              <div>
                <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1">攝影師</label>
                <select value={editForm.photographer_id} onChange={e => setEditForm(f => ({ ...f, photographer_id: e.target.value }))}
                  className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-sm font-light focus:outline-none focus:border-[#C8B8A6] cursor-pointer">
                  <option value="">請選擇攝影師</option>
                  {photographers.map(p => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1">狀態</label>
                <select value={editForm.confirmation_status} onChange={e => setEditForm(f => ({ ...f, confirmation_status: e.target.value as ConfirmationStatus }))}
                  className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-sm font-light focus:outline-none focus:border-[#C8B8A6] cursor-pointer">
                  <option value="pending">已指派</option>
                  <option value="confirmed">已確認</option>
                  <option value="declined">已拒絕</option>
                  <option value="replacement_needed">需替換</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1">備註</label>
                <textarea value={editForm.note} onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))}
                  rows={2} maxLength={500}
                  className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-sm font-light focus:outline-none focus:border-[#C8B8A6] resize-none" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#D9D9D9]/60 flex justify-end gap-2">
              <button onClick={() => setModalOpen(false)} disabled={saving} className="px-4 py-2 text-sm font-light text-[#2C2C2C]/60 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleSaveEdit} disabled={saving || !editForm.photographer_id}
                className="px-4 py-2 bg-[#2C2C2C] text-white text-sm font-light rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-40">
                {saving ? "儲存中..." : "儲存"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Assignment Modal */}
      {addModalOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-lg">
            <div className="px-6 py-4 border-b border-[#D9D9D9]/60">
              <h2 className="text-base font-light text-[#2C2C2C] tracking-wide">新增指派</h2>
            </div>
            <div className="px-6 py-5 flex flex-col gap-4">
              <div>
                <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1">訂單</label>
                <select value={addForm.order_id} onChange={e => setAddForm(f => ({ ...f, order_id: e.target.value }))}
                  className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-sm font-light focus:outline-none focus:border-[#C8B8A6] cursor-pointer">
                  <option value="">請選擇訂單</option>
                  {orders.map(o => <option key={o.id} value={o.id}>{o.order_number} — {o.selected_city} {o.shoot_date}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1">攝影師</label>
                <select value={addForm.photographer_id} onChange={e => setAddForm(f => ({ ...f, photographer_id: e.target.value }))}
                  className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-sm font-light focus:outline-none focus:border-[#C8B8A6] cursor-pointer">
                  <option value="">請選擇攝影師</option>
                  {photographers.map(p => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1">備註</label>
                <textarea value={addForm.note} onChange={e => setAddForm(f => ({ ...f, note: e.target.value }))}
                  rows={2} maxLength={500}
                  className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-sm font-light focus:outline-none focus:border-[#C8B8A6] resize-none" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#D9D9D9]/60 flex justify-end gap-2">
              <button onClick={() => setAddModalOpen(false)} disabled={saving} className="px-4 py-2 text-sm font-light text-[#2C2C2C]/60 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleAddAssignment} disabled={saving || !addForm.order_id || !addForm.photographer_id}
                className="px-4 py-2 bg-[#2C2C2C] text-white text-sm font-light rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-40">
                {saving ? "儲存中..." : "儲存"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
